// old IE etc.
import 'core-js/stable'
import '@design-system/utils/dom';

import './styles/main.scss'

import * as ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { ConnectedRouter } from 'connected-react-router';
import { createBrowserHistory } from 'history';
import configureStore from '@store/configureStore';
import App from './App';
import * as serviceWorker from './serviceWorkerRegistration';
import { ApplicationState } from '@store/index';
import { AppSettings, createPrivatePortal, NodeContextHttp, NodeContextState } from '@store/nodeContextStore';
import { TaskManager } from '@utils/node-utils';
import LocalizationService from '@services/LocalizationService';
import { CookieManager } from '@utils/CookiesUtils';
import { TitleManager } from '@utils/TitleManager';
import EnumDescriptionService from '@services/EnumDescriptionService';
import DataRowsService from '@services/DataRowsService';
import EvaluateConditionsService from '@services/EvaluateConditionsService';
import CompatibilityTestService from '@services/CompatibilityTestService';
import axios from 'axios';
import CryptoService from '@services/CryptoService';
import InstanceService from '@services/InstanceService';
import NENService from '@services/NENService';
import { useApplicationInsights, reactPlugin } from '@utils/ApplicationInsights';
import { AppInsightsContext } from "@microsoft/applicationinsights-react-js";
import PasswordResetService from '@services/PasswordResetService';
import HeartBeatService from '@services/HeartBeatService';
import { DescriptionManager } from '@utils/DescriptionManager';
import WidgetService from '@services/WidgetService';
import { getCurrentLanguage } from '@store/localizationStore';

const req = requirejs; // kdyz to je naprimo, tak to webpack zahodi
req.config({
    bundles: {
        'static/js/TescoSW.Crypto.LightWeb.ES5.js': [
            'multiwebclient/mwcore/tescosw/cryptography/crypto/signingsession',
            'MultiWebClient/MWCore/TescoSW/Cryptography/Crypto/SigningExtensionCommunicator',
            'MultiWebClient/MWCore/TescoSW/Global/Base/Converters/DataConverter',
            'MultiWebClient/MWCore/TescoSW/Cryptography/Crypto/NativeAppStreamReader',
            'LightWebClient/TescoSW/Cryptography/SigningActiveXCommunicator',
            'MultiWebClient/MWCore/TescoSW/Cryptography/Crypto/CryptoExceptions'
        ],
        'bignumber': [
            'bignumber.js',
        ],
    },
    paths: {
        'react': 'js/react',
        'react-dom': 'js/react-dom',
        '@microsoft/applicationinsights-web': 'js/@microsoft/applicationinsights-web',
        'bignumber': 'js/bignumber.min',
        'redux-saga': 'js/redux-saga',
        'redux': 'js/redux',
        'immer': 'js/immer',
        'react-redux': 'js/react-redux',
        'react-virtualized': 'js/react-virtualized',
        'node-forge': 'js/node-forge',
        'numeral/locales': 'js/numeral/locales',
        'numeral': 'js/numeral',
        'overlayscrollbars': 'js/overlayscrollbars',
        'overlayscrollbars-react': 'js/overlayscrollbars-react',
        'prop-types': 'js/prop-types',
        'Reselect': 'js/reselect',
        'formatjs-intl-relativetimeformat/polyfill': 'js/formatjs-intl-relativetimeformat/polyfill',
    }
});

// Create browser history to use in the Redux store
const baseUrl = document.getElementsByTagName('base')[0].getAttribute('href') as string;
const history = createBrowserHistory({ basename: baseUrl });

const getMetaValue = (key: string) => document.querySelector<HTMLMetaElement>(`meta[name=${key}]`)?.content;

let initialState = {} as ApplicationState;
const initialStateMeta = getMetaValue('initialReduxState');
if (initialStateMeta)
    initialState = JSON.parse(decodeURI(initialStateMeta)) as ApplicationState;

const completedTasksMeta = getMetaValue('completedTasks');
if (completedTasksMeta)
    (window as any).completedTasks = JSON.parse(decodeURI(completedTasksMeta));
// Get the application-wide store instance, prepopulating with state from the server where available.

let appSettings = {} as AppSettings;
const appSettingsMeta = getMetaValue('appSettings');
if (appSettingsMeta)
    appSettings = JSON.parse(decodeURI(appSettingsMeta)) as AppSettings;

const publicPortalPath = getMetaValue('publicPortalPath') ?? '';
const privatePortal = createPrivatePortal(getMetaValue('privatePortalPath'));
axios.defaults.baseURL = publicPortalPath;
axios.defaults.withCredentials = true;
const nodeContextHttp: NodeContextHttp = {
    cookies: new CookieManager(),
    getLanguage: () => getCurrentLanguage(store.getState()),
}

const nodeContext: NodeContextState = {
    http: nodeContextHttp,
    taskManager: new TaskManager(),
    descriptionManager: new DescriptionManager(),
    localizationService: new LocalizationService(nodeContextHttp),
    titleManager: new TitleManager(),
    enumDescriptionService: new EnumDescriptionService(nodeContextHttp),
    dataRowsService: new DataRowsService(nodeContextHttp),
    evaluateConditionsService: new EvaluateConditionsService(nodeContextHttp),
    compatibilityTestService: new CompatibilityTestService(nodeContextHttp),
    cryptoService: new CryptoService(nodeContextHttp),
    instanceService: new InstanceService(nodeContextHttp),
    nenService: new NENService(nodeContextHttp),
    passwordResetService: new PasswordResetService(nodeContextHttp),
    heartBeatService: new HeartBeatService(nodeContextHttp),
    widgetService: new WidgetService(nodeContextHttp),
    privatePortal,
    appSettings
}

const { store } = configureStore(history, nodeContext, initialState);

useApplicationInsights(history, nodeContext, appSettings.appInsightsKey);

ReactDOM.hydrate(
    <Provider store={store}>
        <ConnectedRouter history={history}>
            <AppInsightsContext.Provider value={reactPlugin}>
                <App />
            </AppInsightsContext.Provider>
        </ConnectedRouter>
    </Provider>,
    document.getElementById('root'));

serviceWorker.register();